import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Zap,
  Wrench,
  Rocket,
  Building2,
  Check,
  Phone,
  Sun,
  Cpu,
  BatteryCharging,
} from 'lucide-react';
import CertificationsSection from '../../components/Certifications';
import { useT } from '../../i18n/LanguageContext';

const heroImg =
  'https://images.unsplash.com/photo-1600132806370-bf17e65e942f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxlbmdpbmVlcmluZyUyMGJsdWVwcmludGVufDB8fHx8MTc4NDgxNjg5OHww&ixlib=rb-4.1.0&q=85';

const content = {
  fr: {
    back: 'Tous nos services',
    badge: 'SERVICE · COURANTS FAIBLES',
    title: 'Courants',
    accent: 'faibles.',
    hero:
      'Vidéosurveillance, contrôle d’accès, téléphonie, réseaux VDI et systèmes de sécurité pour vos bâtiments tertiaires et habitations.',
    home: 'Accueil',
    services: 'Services',
    current: 'Courants faibles',

    expertise: 'NOTRE EXPERTISE',
    expertiseTitle: 'Ce que nous réalisons pour vous',

    description:
      'Vous souhaitez mettre en place un système de surveillance vidéo, d’interphonie ou changer le système de téléphonie en place au sein de vos locaux. Faites appel à nous en installation électrique courant faible. Notre équipe de professionnels est intervenue sur des projets labellisés tertiaires et habitations. Vous aurez la certitude d’avoir des installations sécurisées et en adéquation avec les dernières normes en vigueur.',

    methodologyTitle: 'Méthodologie & accompagnement',
    methodology: [
      'Études, conception et dimensionnement',
      'Installation, maintenance et dépannage',
      'Accompagnement, assistance et conseils personnalisés',
      'Mise en conformité avec les dernières normes en vigueur',
    ],

    solutionsTitle: 'Nos solutions',
    solutions: [
      'Téléphonie, informatique et internet',
      'Vidéosurveillance, alarme et contrôle d’accès',
      'Systèmes de sécurité incendie (SSI / SSA)',
      'Solutions de domotique et interphonie',
      'Automatismes dans le milieu industriel',
      'GTB / GTC (gestion technique du bâtiment)',
      'Réseaux VDI et câblage structuré',
      'Sonorisation et diffusion d’information',
    ],

    sectorsTitle: 'Secteurs d’intervention',
    sectors: [
      'Tertiaire / Bureaux',
      'Habitations / Résidentiel',
      'Industriel / Logistique',
      'Collectivités / Établissements publics',
    ],

    ctaTitle: 'Un projet courants faibles ?',
    ctaText:
      'Obtenez une étude personnalisée et un devis gratuit sous 24h.',
    quote: 'Demander un devis',
    call: 'Nous appeler',

    relatedTitle: 'Services complémentaires',
    related: [
      {
        slug: 'courants-forts',
        title: 'Courants forts',
        desc: 'Installations électriques BT/HT tertiaires et industrielles.',
        icon: Zap,
      },
      {
        slug: 'energies-renouvelables',
        title: 'Énergies renouvelables',
        desc: 'Solutions photovoltaïques clé en main, de l’étude à l’exploitation.',
        icon: Sun,
      },
      {
        slug: 'mobilite-electrique',
        title: 'Mobilité électrique',
        desc: 'Conception et déploiement d’infrastructures de recharge (IRVE).',
        icon: BatteryCharging,
      },
    ],
  },

  en: {
    back: 'All our services',
    badge: 'SERVICE · LOW-VOLTAGE SYSTEMS',
    title: 'Low-voltage',
    accent: 'systems.',
    hero:
      'Video surveillance, access control, telephony, VDI networks and security systems for commercial buildings and residential properties.',
    home: 'Home',
    services: 'Services',
    current: 'Low-voltage systems',

    expertise: 'OUR EXPERTISE',
    expertiseTitle: 'What we deliver for you',

    description:
      'Whether you need to install a video surveillance or intercom system, or upgrade the telephone system in your premises, our low-voltage electrical installation team can help. Our professionals have worked on commercial and residential projects, providing secure installations that comply with the latest applicable standards.',

    methodologyTitle: 'Methodology & support',
    methodology: [
      'Studies, design and sizing',
      'Installation, maintenance and troubleshooting',
      'Personalised support, assistance and consulting',
      'Compliance with the latest applicable standards',
    ],

    solutionsTitle: 'Our solutions',
    solutions: [
      'Telephony, IT and internet',
      'Video surveillance, alarms and access control',
      'Fire safety systems (SSI / SSA)',
      'Building automation and intercom systems',
      'Industrial automation',
      'Building management systems (BMS)',
      'VDI networks and structured cabling',
      'Public address and information broadcasting',
    ],

    sectorsTitle: 'Industries we serve',
    sectors: [
      'Commercial / Offices',
      'Residential / Housing',
      'Industrial / Logistics',
      'Public authorities / Public facilities',
    ],

    ctaTitle: 'Have a low-voltage project?',
    ctaText:
      'Get a personalised study and a free quotation within 24 hours.',
    quote: 'Request a quotation',
    call: 'Call us',

    relatedTitle: 'Related services',
    related: [
      {
        slug: 'courants-forts',
        title: 'Power systems',
        desc: 'Commercial and industrial LV/MV electrical installations.',
        icon: Zap,
      },
      {
        slug: 'energies-renouvelables',
        title: 'Renewable energy',
        desc: 'Turnkey photovoltaic solutions, from study to operation.',
        icon: Sun,
      },
      {
        slug: 'mobilite-electrique',
        title: 'Electric mobility',
        desc: 'Design and deployment of EV charging infrastructure.',
        icon: BatteryCharging,
      },
    ],
  },

  ar: {
    back: 'جميع خدماتنا',
    badge: 'الخدمة · أنظمة التيارات الضعيفة',
    title: 'التيارات',
    accent: 'الضعيفة.',
    hero:
      'المراقبة بالفيديو والتحكم في الدخول والاتصالات وشبكات VDI وأنظمة الأمن للمباني التجارية والسكنية.',
    home: 'الرئيسية',
    services: 'الخدمات',
    current: 'التيارات الضعيفة',

    expertise: 'خبرتنا',
    expertiseTitle: 'ما نقدمه لكم',

    description:
      'إذا كنتم ترغبون في تركيب نظام للمراقبة بالفيديو أو الاتصال الداخلي أو تحديث نظام الاتصالات في منشآتكم، يمكنكم الاعتماد على خبرتنا في أنظمة التيارات الضعيفة. عمل فريقنا المتخصص على مشاريع تجارية وسكنية، مع ضمان تنفيذ تركيبات آمنة ومتوافقة مع أحدث المعايير المعمول بها.',

    methodologyTitle: 'المنهجية والمرافقة',
    methodology: [
      'الدراسات والتصميم والحسابات الفنية',
      'التركيب والصيانة وإصلاح الأعطال',
      'المرافقة والمساعدة والاستشارات المخصصة',
      'مطابقة الأنظمة مع أحدث المعايير المعمول بها',
    ],

    solutionsTitle: 'حلولنا',
    solutions: [
      'أنظمة الاتصالات وتقنية المعلومات والإنترنت',
      'المراقبة بالفيديو والإنذارات والتحكم في الدخول',
      'أنظمة السلامة والحماية من الحرائق',
      'أنظمة أتمتة المباني والاتصال الداخلي',
      'أنظمة الأتمتة الصناعية',
      'أنظمة إدارة المباني والتحكم الفني',
      'شبكات VDI والكابلات المنظمة',
      'أنظمة الصوت وبث المعلومات',
    ],

    sectorsTitle: 'قطاعات التدخل',
    sectors: [
      'القطاع التجاري / المكاتب',
      'السكن / المباني السكنية',
      'القطاع الصناعي / الخدمات اللوجستية',
      'الهيئات المحلية / المنشآت العامة',
    ],

    ctaTitle: 'هل لديكم مشروع للتيارات الضعيفة؟',
    ctaText:
      'احصلوا على دراسة مخصصة وعرض سعر مجاني خلال 24 ساعة.',
    quote: 'طلب عرض سعر',
    call: 'اتصل بنا',

    relatedTitle: 'الخدمات ذات الصلة',
    related: [
      {
        slug: 'courants-forts',
        title: 'التيارات القوية',
        desc: 'التركيبات الكهربائية للمباني التجارية والصناعية.',
        icon: Zap,
      },
      {
        slug: 'energies-renouvelables',
        title: 'الطاقات المتجددة',
        desc: 'حلول الطاقة الشمسية المتكاملة من الدراسة إلى التشغيل.',
        icon: Sun,
      },
      {
        slug: 'mobilite-electrique',
        title: 'التنقل الكهربائي',
        desc: 'تصميم وتنفيذ البنية التحتية لشحن السيارات الكهربائية.',
        icon: BatteryCharging,
      },
    ],
  },
};

const CheckItem = ({ children }) => (
  <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-[#0b3b8c]/30 hover:shadow-sm transition-all">
    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center shrink-0">
      <Check className="w-3 h-3" strokeWidth={3.5} />
    </span>

    <span className="text-slate-700 text-sm">
      {children}
    </span>
  </div>
);

const SectionTitle = ({ Icon, children }) => (
  <div className="flex items-center gap-3">
    <span className="w-9 h-9 rounded-lg bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center">
      <Icon className="w-4 h-4" strokeWidth={2.4} />
    </span>

    <h3 className="text-[#0b1c3a] font-bold text-lg md:text-xl tracking-tight">
      {children}
    </h3>
  </div>
);

const CourantsFaibles = () => {
  const { lang } = useT();

  const currentLang = ['fr', 'en', 'ar'].includes(lang)
    ? lang
    : 'fr';

  const c = content[currentLang];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      className="bg-white"
      dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
    >

      {/* HERO */}
      <section className="relative pt-40 pb-24 md:pt-48 md:pb-28 overflow-hidden">

        <img
          src={heroImg}
          alt={c.current}
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1c3a]/95 via-[#0b1c3a]/80 to-[#0b1c3a]/95" />

        <div className="absolute right-[-10%] top-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full bg-[#0b3b8c] blur-[140px] opacity-40 pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">

          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            {c.back}
          </Link>

          <div className="mt-8 flex items-center gap-4">

            <div className="w-12 h-12 rounded-xl bg-[#0b3b8c] flex items-center justify-center border border-white/10">
              <Cpu
                className="w-6 h-6 text-white"
                strokeWidth={2.4}
              />
            </div>

            <span className="px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white/90 text-[11px] tracking-[0.25em] font-semibold">
              {c.badge}
            </span>

          </div>

          <h1 className="mt-6 text-white font-black leading-[1.05] tracking-tight text-4xl md:text-6xl lg:text-7xl max-w-4xl">

            {c.title}{' '}

            <span className="italic font-serif text-[#f5c518]">
              {c.accent}
            </span>

          </h1>

          <p className="mt-6 text-white/85 text-base md:text-lg max-w-2xl leading-relaxed">
            {c.hero}
          </p>

          <nav
            aria-label="breadcrumb"
            className="mt-8 flex items-center gap-2 text-white/70 text-sm"
          >
            <Link
              to="/"
              className="hover:text-white transition-colors"
            >
              {c.home}
            </Link>

            <span>/</span>

            <Link
              to="/services"
              className="hover:text-white transition-colors"
            >
              {c.services}
            </Link>

            <span>/</span>

            <span className="text-[#f5c518] font-semibold">
              {c.current}
            </span>
          </nav>

        </div>

        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-white" />

      </section>

      {/* MAIN GRID */}
      <section className="pb-24">

        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_360px] gap-10 lg:gap-14 items-start">

          <div>

            <span className="inline-block px-3 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 text-[11px] tracking-[0.25em] font-semibold">
              {c.expertise}
            </span>

            <h2 className="mt-5 text-3xl md:text-4xl font-black text-[#0b1c3a] leading-tight tracking-tight">
              {c.expertiseTitle}
            </h2>

            <div className="mt-6 p-5 md:p-6 rounded-2xl bg-slate-50 border-l-4 border-[#0b3b8c] text-slate-700 leading-relaxed">
              {c.description}
            </div>

            {/* METHODOLOGY */}
            <div className="mt-10">

              <SectionTitle Icon={Wrench}>
                {c.methodologyTitle}
              </SectionTitle>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">

                {c.methodology.map((item) => (
                  <CheckItem key={item}>
                    {item}
                  </CheckItem>
                ))}

              </div>

            </div>

            {/* SOLUTIONS */}
            <div className="mt-12">

              <SectionTitle Icon={Rocket}>
                {c.solutionsTitle}
              </SectionTitle>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">

                {c.solutions.map((item) => (
                  <CheckItem key={item}>
                    {item}
                  </CheckItem>
                ))}

              </div>

            </div>

            {/* SECTORS */}
            <div className="mt-12">

              <SectionTitle Icon={Building2}>
                {c.sectorsTitle}
              </SectionTitle>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">

                {c.sectors.map((item) => (
                  <CheckItem key={item}>
                    {item}
                  </CheckItem>
                ))}

              </div>

            </div>

          </div>

          {/* CTA */}
          <aside className="lg:sticky lg:top-28">

            <div className="relative bg-[#0b1c3a] text-white rounded-3xl p-7 shadow-xl overflow-hidden">

              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#0b3b8c] blur-[70px] opacity-60" />

              <div className="relative">

                <div className="w-11 h-11 rounded-xl bg-[#f5c518] text-[#0b1c3a] flex items-center justify-center">
                  <Cpu
                    className="w-5 h-5"
                    strokeWidth={2.4}
                  />
                </div>

                <h3 className="mt-5 text-xl font-bold leading-tight">
                  {c.ctaTitle}
                </h3>

                <p className="mt-3 text-white/70 text-sm leading-relaxed">
                  {c.ctaText}
                </p>

                <Link
                  to="/contact"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#0b3b8c] hover:bg-[#082e6f] font-semibold text-sm transition-colors"
                >
                  {c.quote}
                  <ArrowRight className="w-4 h-4" />
                </Link>

                <a
                  href="tel:+33442072262"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white text-[#0b1c3a] hover:bg-slate-100 font-semibold text-sm transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  {c.call}
                </a>

              </div>

            </div>

          </aside>

        </div>

      </section>

      {/* CERTIFICATIONS */}
      <CertificationsSection />

      {/* RELATED SERVICES */}
      <section className="py-20 bg-[#f7f8fb] border-t border-slate-200">

        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          <h2 className="text-2xl md:text-3xl font-black text-[#0b1c3a] tracking-tight">
            {c.relatedTitle}
          </h2>

          <div className="mt-8 grid md:grid-cols-3 gap-4">

            {c.related.map((r) => {

              const Icon = r.icon;

              return (
                <Link
                  key={r.slug}
                  to={`/services/${r.slug}`}
                  className="group bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4 hover:shadow-md hover:border-[#0b3b8c]/30 transition-all"
                >

                  <div className="shrink-0 w-11 h-11 rounded-xl bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center group-hover:bg-[#0b3b8c] group-hover:text-white transition-colors">
                    <Icon
                      className="w-5 h-5"
                      strokeWidth={2.2}
                    />
                  </div>

                  <div className="min-w-0 flex-1">

                    <div className="text-[#0b1c3a] font-bold text-sm">
                      {r.title}
                    </div>

                    <div className="text-slate-500 text-xs mt-1 line-clamp-1">
                      {r.desc}
                    </div>

                  </div>

                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#0b3b8c] transition-colors" />

                </Link>
              );

            })}

          </div>

        </div>

      </section>

    </main>
  );
};

export default CourantsFaibles;